import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Users, Building2, DollarSign, Activity, TrendingUp, AlertCircle } from 'lucide-react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, LineChart, Line, PieChart, Pie, Cell } from 'recharts';

const statsData = [
  { label: 'Total Clients', value: '147', change: '+12%', icon: Users, color: 'bg-blue-500' },
  { label: 'Active Subscriptions', value: '132', change: '+8%', icon: Building2, color: 'bg-green-500' },
  { label: 'Monthly Revenue', value: '$48,320', change: '+23%', icon: DollarSign, color: 'bg-purple-500' },
  { label: 'Total API Calls (Today)', value: '2.4M', change: '+5%', icon: Activity, color: 'bg-orange-500' },
];

const monthlyData = [
  { month: 'Jan', clients: 98, revenue: 32400 },
  { month: 'Feb', clients: 105, revenue: 35100 },
  { month: 'Mar', clients: 112, revenue: 37800 },
  { month: 'Apr', clients: 118, revenue: 40200 },
  { month: 'May', clients: 125, revenue: 42500 },
  { month: 'Jun', clients: 132, revenue: 45600 },
  { month: 'Jul', clients: 138, revenue: 46800 },
  { month: 'Aug', clients: 147, revenue: 48320 },
];

const erpDistribution = [
  { name: 'SAP', value: 42, color: '#0052CC' },
  { name: 'Oracle', value: 28, color: '#36B37E' },
  { name: 'Tally', value: 35, color: '#FFAB00' },
  { name: 'Zoho', value: 22, color: '#6554C0' },
  { name: 'Dynamics', value: 20, color: '#FF5630' },
];

const recentActivity = [
  { client: 'ABC Trading LLC', action: 'New subscription activated', time: '5 mins ago', type: 'success' },
  { client: 'XYZ Corp', action: 'API limit exceeded', time: '12 mins ago', type: 'warning' },
  { client: 'Global Enterprises', action: 'Payment received', time: '1 hour ago', type: 'success' },
  { client: 'Tech Solutions FZ', action: 'Subscription expiring soon', time: '2 hours ago', type: 'alert' },
];

export default function SuperAdminOverview() {
  return (
    <div className="p-6 lg:p-8 space-y-6 bg-mesh-gradient min-h-screen">
      {/* Header */}
      <div className="fade-in">
        <div className="flex items-center gap-3 mb-2">
          <div className="relative">
            <div className="absolute inset-0 bg-gradient-to-br from-purple-600 to-pink-600 rounded-xl blur-md opacity-50"></div>
            <div className="relative p-3 bg-gradient-to-br from-purple-600 to-pink-600 rounded-xl premium-shadow">
              <Activity className="w-6 h-6 text-white" />
            </div>
          </div>
          <div>
            <h1 className="text-4xl text-gray-900">Super Admin Dashboard</h1>
            <p className="text-gray-600 mt-1">Monitor and manage your entire AIS Connect platform</p>
          </div>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 slide-up">
        {statsData.map((stat, index) => {
          const Icon = stat.icon;
          return (
            <Card 
              key={stat.label} 
              className="relative overflow-hidden premium-shadow hover-lift group"
              style={{ animationDelay: `${index * 100}ms` }}
            >
              <div className={`absolute top-0 right-0 w-32 h-32 ${stat.color.replace('bg-', 'bg-').replace('-500', '-100')} rounded-full -translate-y-16 translate-x-16 opacity-30 group-hover:scale-150 transition-transform duration-500`}></div>
              <CardContent className="p-6 relative z-10">
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <p className="text-sm text-gray-600 uppercase tracking-wide mb-1">{stat.label}</p>
                    <p className="text-4xl text-gray-900 mt-2">{stat.value}</p>
                    <div className="flex items-center mt-3 text-green-600">
                      <TrendingUp className="w-4 h-4 mr-1" />
                      <span className="text-sm">{stat.change}</span>
                      <span className="text-xs text-gray-500 ml-1">this month</span>
                    </div>
                  </div>
                  <div className={`${stat.color} w-14 h-14 rounded-xl flex items-center justify-center shadow-lg group-hover:scale-110 transition-transform`}>
                    <Icon className="w-7 h-7 text-white" />
                  </div>
                </div>
              </CardContent>
            </Card>
          );
        })}
      </div>

      {/* Charts Row */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Client Growth Chart */}
        <Card className="premium-shadow-lg hover-lift overflow-hidden">
          <CardHeader className="border-b bg-gradient-to-r from-blue-50 via-indigo-50 to-purple-50">
            <div className="flex items-center gap-2">
              <div className="p-2 bg-white rounded-lg shadow-sm">
                <TrendingUp className="w-5 h-5 text-blue-600" />
              </div>
              <CardTitle className="text-xl">Client Growth & Revenue</CardTitle>
            </div>
          </CardHeader>
          <CardContent className="pt-8 pb-6">
            <ResponsiveContainer width="100%" height={300}>
              <LineChart data={monthlyData}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="month" />
                <YAxis yAxisId="left" />
                <YAxis yAxisId="right" orientation="right" />
                <Tooltip />
                <Line yAxisId="left" type="monotone" dataKey="clients" stroke="#0052CC" strokeWidth={2} />
                <Line yAxisId="right" type="monotone" dataKey="revenue" stroke="#36B37E" strokeWidth={2} />
              </LineChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>

        {/* ERP Distribution */}
        <Card>
          <CardHeader>
            <CardTitle>ERP System Distribution</CardTitle>
          </CardHeader>
          <CardContent>
            <ResponsiveContainer width="100%" height={300}>
              <PieChart>
                <Pie
                  data={erpDistribution}
                  cx="50%"
                  cy="50%"
                  labelLine={false}
                  label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}
                  outerRadius={100}
                  fill="#8884d8"
                  dataKey="value"
                >
                  {erpDistribution.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={entry.color} />
                  ))}
                </Pie>
                <Tooltip />
              </PieChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>
      </div>

      {/* Recent Activity */}
      <Card>
        <CardHeader>
          <CardTitle>Recent Activity</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            {recentActivity.map((activity, index) => (
              <div key={index} className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
                <div className="flex items-center space-x-4">
                  <div className={`w-2 h-2 rounded-full ${
                    activity.type === 'success' ? 'bg-green-500' :
                    activity.type === 'warning' ? 'bg-yellow-500' :
                    'bg-red-500'
                  }`}></div>
                  <div>
                    <p className="text-gray-900">{activity.client}</p>
                    <p className="text-sm text-gray-600">{activity.action}</p>
                  </div>
                </div>
                <p className="text-sm text-gray-500">{activity.time}</p>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
